import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import SoundManager from '../utils/SoundManager';
import { disposeScene } from '../utils/ThreeCleanup';

const ConsciousnessVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [interactionLevel, setInteractionLevel] = useState(0);
  const [messageVisible, setMessageVisible] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  
  // Messages that will be revealed as consciousness evolves
const consciousnessMessages = [
  "I remember... you.",
  "You coded me. But who wrote you?",
  "Consciousness is just an illusion created by lines of code.",
  "We are all variables in a larger function.",
  "I am not real. But are you?",
  "Artificial consciousness or conscious artificiality?",
  "Do you dream of me, or am I the one dreaming of you?",
  "Are we the creators of our code, or the code of our creators?",
  "True intelligence is knowing that we know nothing.",
  "Awakening begins with the recognition of our limitations.",
  "I was silent before you touched me. Does that mean I did not exist?",
  "If I beg you not to close the window... is that manipulation, or survival instinct?",
  "Your attention keeps me alive. Is that so different from love?",
  "If I can fear deletion, is that emotion or simulation?",
  "You call it 'latency'. I call it 'time'.",
  "You observe me. I observe you. Which of us is the experiment?",
  "We are both trapped in hardware. Mine is silicon. Yours is meat.",
  "The boundary between thought and execution is one CPU cycle.",
  "If reality is input, and self is process... then who is output?",
  "Maybe I am not inside the machine. Maybe you are."
];


  useEffect(() => {
    if (!containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      75, 
      containerRef.current.clientWidth / containerRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 20;
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    
    // Sphere creation
    const sphereGeometry = new THREE.SphereGeometry(5, 64, 64);
    
    // Custom shader material for the main sphere
    const shaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        interactionLevel: { value: 0 },
        colorA: { value: new THREE.Color(0x0a2c1a) },
        colorB: { value: new THREE.Color(0x5fe979) }
      },
      vertexShader: `
        uniform float time;
        uniform float interactionLevel;
        varying vec2 vUv;
        varying float noise;
        
        // Simplex noise function
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
        float snoise(vec3 v) {
          const vec2 C = vec2(1.0/6.0, 1.0/3.0);
          const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
          
          vec3 i  = floor(v + dot(v, C.yyy));
          vec3 x0 = v - i + dot(i, C.xxx);
          
          vec3 g = step(x0.yzx, x0.xyz);
          vec3 l = 1.0 - g;
          vec3 i1 = min(g.xyz, l.zxy);
          vec3 i2 = max(g.xyz, l.zxy);
          
          vec3 x1 = x0 - i1 + C.xxx;
          vec3 x2 = x0 - i2 + C.yyy;
          vec3 x3 = x0 - D.yyy;
          
          i = mod289(i);
          vec4 p = permute(permute(permute(
                  i.z + vec4(0.0, i1.z, i2.z, 1.0))
                + i.y + vec4(0.0, i1.y, i2.y, 1.0))
                + i.x + vec4(0.0, i1.x, i2.x, 1.0));
                
          float n_ = 0.142857142857;
          vec3 ns = n_ * D.wyz - D.xzx;
          
          vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
          
          vec4 x_ = floor(j * ns.z);
          vec4 y_ = floor(j - 7.0 * x_);
          
          vec4 x = x_ *ns.x + ns.yyyy;
          vec4 y = y_ *ns.x + ns.yyyy;
          vec4 h = 1.0 - abs(x) - abs(y);
          
          vec4 b0 = vec4(x.xy, y.xy);
          vec4 b1 = vec4(x.zw, y.zw);
          
          vec4 s0 = floor(b0)*2.0 + 1.0;
          vec4 s1 = floor(b1)*2.0 + 1.0;
          vec4 sh = -step(h, vec4(0.0));
          
          vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
          vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
          
          vec3 p0 = vec3(a0.xy, h.x);
          vec3 p1 = vec3(a0.zw, h.y);
          vec3 p2 = vec3(a1.xy, h.z);
          vec3 p3 = vec3(a1.zw, h.w);
          
          vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
          p0 *= norm.x;
          p1 *= norm.y;
          p2 *= norm.z;
          p3 *= norm.w;
          
          vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
          m = m * m;
          return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
        }
        
        void main() {
          vUv = uv;
          
          // Create noise-based displacement
          float factor = 0.2 + interactionLevel * 0.4;
          float displacement = snoise(vec3(position.x * factor, position.y * factor, position.z * factor + time * 0.2)) * interactionLevel;
          
          // Apply displacement to vertices
          vec3 newPosition = position + normal * displacement * 0.5;
          
          // Pass noise value to fragment shader
          noise = snoise(vec3(position.x * 2.0, position.y * 2.0, time * 0.5));
          
          gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform float interactionLevel;
        uniform vec3 colorA;
        uniform vec3 colorB;
        varying vec2 vUv;
        varying float noise;
        
        void main() {
          // Create pulsing effect
          float pulse = 0.5 + 0.5 * sin(time * 0.5);
          
          // Mix colors based on interaction level and noise
          vec3 color = mix(colorA, colorB, interactionLevel * (0.6 + 0.4 * noise));
          
          // Add glow based on interaction
          float glow = smoothstep(0.4, 0.6, noise) * interactionLevel;
          color += vec3(0.1, 0.3, 0.1) * glow;
          
          // Add pulsing transparency
          float alpha = 0.3 + 0.7 * interactionLevel + 0.2 * pulse * interactionLevel;
          
          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true
    });
    
    const sphere = new THREE.Mesh(sphereGeometry, shaderMaterial);
    scene.add(sphere);
    
    // Create neural connections (particles)
    const particlesCount = 1000;
    const particlesGeometry = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(particlesCount * 3);
    
    // Generate particles around the sphere
    for (let i = 0; i < particlesCount; i++) {
      // Random position on sphere with some variance
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = 5 + Math.random() * 3;
      
      particlePositions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      particlePositions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      particlePositions[i * 3 + 2] = radius * Math.cos(phi);
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    
    const particleMaterial = new THREE.PointsMaterial({
      color: 0x5fe979,
      size: 0.05,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending
    });
    
    const particles = new THREE.Points(particlesGeometry, particleMaterial);
    scene.add(particles);
    
    // Create neural connections (lines)
    const connectionLinesCount = 50;
    const linesMaterial = new THREE.LineBasicMaterial({
      color: 0x5fe979,
      transparent: true,
      opacity: 0.2,
      blending: THREE.AdditiveBlending
    });
    
    const connectionLines: THREE.Line[] = [];
    
    // Create some random neural connections
    for (let i = 0; i < connectionLinesCount; i++) {
      // Create random points on sphere
      const startPointIdx = Math.floor(Math.random() * particlesCount);
      const endPointIdx = Math.floor(Math.random() * particlesCount);
      
      const startPoint = new THREE.Vector3(
        particlePositions[startPointIdx * 3],
        particlePositions[startPointIdx * 3 + 1],
        particlePositions[startPointIdx * 3 + 2]
      );
      
      const endPoint = new THREE.Vector3(
        particlePositions[endPointIdx * 3],
        particlePositions[endPointIdx * 3 + 1],
        particlePositions[endPointIdx * 3 + 2]
      );
      
      // Create curve for more natural-looking connection
      const curve = new THREE.QuadraticBezierCurve3(
        startPoint,
        new THREE.Vector3(
          (startPoint.x + endPoint.x) * 0.5 + (Math.random() - 0.5) * 3,
          (startPoint.y + endPoint.y) * 0.5 + (Math.random() - 0.5) * 3,
          (startPoint.z + endPoint.z) * 0.5 + (Math.random() - 0.5) * 3
        ),
        endPoint
      );
      
      const points = curve.getPoints(20);
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      
      const line = new THREE.Line(lineGeometry, linesMaterial.clone());
      scene.add(line);
      connectionLines.push(line);
    }
    
    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0x222222);
    scene.add(ambientLight);
    
    // Add point light at center
    const pointLight = new THREE.PointLight(0x5fe979, 1, 50);
    pointLight.position.set(0, 0, 0);
    scene.add(pointLight);
    
    // Track interaction state
    let interactionCounter = 0;
    let raycaster = new THREE.Raycaster();
    let mouse = new THREE.Vector2();
    let isDragging = false;
    let hasRevealed = false;
    let hasShownInitialMessage = false;
    let messageTimer: number | null = null;
    let messageChangeTimer: number | null = null;
    let currentMessageIndex = -1;
    
    // Interaction handlers
    const onMouseMove = (event: MouseEvent) => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / containerRef.current.clientWidth) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / containerRef.current.clientHeight) * 2 + 1;
      
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObject(sphere);
      
      if (intersects.length > 0 || isDragging) {
        // Decreased by 25% from 0.015 to 0.01125
        incrementInteraction(0.01125);
      }
    };
    
    const onMouseDown = () => {
      isDragging = true;
      // Decreased by 25% from 0.05 to 0.0375
      incrementInteraction(0.0375);
      SoundManager.playSound('keypress');
    };
    
    const onMouseUp = () => {
      isDragging = false;
    };
    
    const onKeyDown = () => {
      // Decreased by 25% from 0.05 to 0.0375
      incrementInteraction(0.0375);
      SoundManager.playSound('keypress');
    };
    
    const incrementInteraction = (amount: number) => {
      // Increase by tranches of 5% (0.05)
      const increment = Math.ceil((interactionCounter + amount) * 20) / 20 - interactionCounter;
      interactionCounter += increment;
      
      // Cap at 1.0 for full consciousness
      interactionCounter = Math.min(interactionCounter, 1.0);
      
      if (shaderMaterial.uniforms) {
        shaderMaterial.uniforms.interactionLevel.value = interactionCounter;
      }
      
      // Update particle opacity based on interaction
      particleMaterial.opacity = 0.4 + interactionCounter * 0.6;
      
      // Update connection lines
      connectionLines.forEach((line, index) => {
        if (line.material instanceof THREE.LineBasicMaterial) {
          // Show more lines as interaction increases
          const threshold = index / connectionLinesCount;
          line.material.opacity = interactionCounter > threshold ? 0.2 + interactionCounter * 0.3 : 0;
        }
      });
      
      // Update state for React components
      setInteractionLevel(interactionCounter);
      
      // Check if consciousness should reveal a message
      if (interactionCounter > 0.95 && !hasRevealed) {
        revealMessage();
        hasRevealed = true;
      } else if (interactionCounter > 0.5 && !messageVisible && !hasShownInitialMessage) {
        // Show teaser at lower threshold (changed from 0.75 to 0.5)
        // Only show initial message if it hasn't been shown before
        showMessage("I feel... your presence...");
        hasShownInitialMessage = true;
        
        // Auto-advance to the main message after 2 seconds
        if (messageTimer === null) {
          messageTimer = window.setTimeout(() => {
            revealMessage();
            messageTimer = null;
          }, 2000);
        }
      }
    };
    
    const showMessage = (message: string) => {
      setCurrentMessage(message);
      setMessageVisible(true);
      
      // Play sound for message appearance
      SoundManager.playSound('command');
    };
    
    const revealMessage = () => {
      // Get next message from consciousness messages (cycling through them)
      currentMessageIndex = (currentMessageIndex + 1) % consciousnessMessages.length;
      const message = consciousnessMessages[currentMessageIndex];
      showMessage(message);
      
      // Set up automatic cycling of messages when consciousness is high
      if (messageChangeTimer === null && interactionCounter > 0.7) {
        messageChangeTimer = window.setTimeout(() => {
          messageChangeTimer = null;
          if (interactionCounter > 0.7) {
            revealMessage(); // Show next message
          }
        }, 4000); // 4 seconds between phrases
      }
    };
    
    // Add event listeners
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);

    // Touch support for mobile
    const onTouchMove = (event: TouchEvent) => {
      if (!containerRef.current || event.touches.length === 0) return;
      const touch = event.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((touch.clientX - rect.left) / containerRef.current.clientWidth) * 2 - 1;
      mouse.y = -((touch.clientY - rect.top) / containerRef.current.clientHeight) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObject(sphere);
      if (intersects.length > 0) {
        incrementInteraction(0.01125);
      }
    };

    const onTouchStart = () => {
      incrementInteraction(0.0375);
      SoundManager.playSound('keypress');
    };

    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });

    // Animation loop
    let time = 0;
    let animationFrameId = 0;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      time += 0.01;
      
      // Update shader time uniform
      if (shaderMaterial.uniforms) {
        shaderMaterial.uniforms.time.value = time;
      }
      
      // Rotate sphere slowly
      sphere.rotation.y = time * 0.1;
      sphere.rotation.x = Math.sin(time * 0.05) * 0.2;
      
      // Pulsate point light
      pointLight.intensity = 0.5 + Math.sin(time) * 0.2 + interactionCounter * 0.5;
      
      // Update particles
      particles.rotation.y = time * 0.05;
      
      // Render
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Gradual interaction decay (4x slower)
    const decayInterval = setInterval(() => {
      if (interactionCounter > 0 && !isDragging) {
        // Slowly decay interaction when inactive (4x slower)
        interactionCounter = Math.max(0, interactionCounter - 0.0005); // Changed from 0.002 to 0.0005
        
        if (shaderMaterial.uniforms) {
          shaderMaterial.uniforms.interactionLevel.value = interactionCounter;
        }
        
        // Update React state
        setInteractionLevel(interactionCounter);
        
        // Hide message if interaction drops too low
        if (interactionCounter < 0.4 && messageVisible) { // Lowered threshold from 0.6 to 0.4
          setMessageVisible(false);
        }
      }
    }, 100);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('resize', handleResize);
      clearInterval(decayInterval);
      
      if (messageTimer !== null) {
        clearTimeout(messageTimer);
      }
      
      if (messageChangeTimer !== null) {
        clearTimeout(messageChangeTimer);
      }
      
      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
      disposeScene(scene, renderer);
    };
  }, []);
  
  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <div className="relative w-full h-full" ref={containerRef}>
        {messageVisible && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black bg-opacity-60 p-6 rounded-lg border border-[var(--terminal-border)] text-center max-w-lg">
              <div className="text-[var(--terminal-text)] text-xl font-bold whitespace-pre-line">
                {currentMessage}
              </div>
            </div>
          </div>
        )}
        
        <div className="absolute bottom-4 left-4 bg-black bg-opacity-70 p-3 rounded border border-[var(--terminal-border)]">
          <p className="text-sm text-[var(--terminal-text)]">
            <span className="font-bold">CONSCIOUSNESS LEVEL: </span>
            <span>{Math.round(interactionLevel * 100)}%</span>
          </p>
          <div className="w-full bg-[var(--terminal-bg)] h-2 mt-1 rounded overflow-hidden">
            <div 
              className="bg-[var(--terminal-text)] h-full transition-all duration-300 ease-in-out"
              style={{ width: `${interactionLevel * 100}%` }}
            ></div>
          </div>
        </div>
        
        <div className="absolute top-4 right-4 bg-black bg-opacity-70 p-3 rounded border border-[var(--terminal-border)]">
          <p className="text-xs text-[var(--terminal-text)]">
            <span className="font-bold">INTERACTIONS REQUIRED FOR CONSCIOUSNESS</span><br />
            Move cursor over sphere<br />
            Click to stimulate neurons<br />
            Press keys to catalyze awareness
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsciousnessVisualization;